import { AVATAR_STYLES, DEFAULT_AVATAR_STYLE, DICEBEAR_API_BASE } from "@/constants/avatar";
import type { AvatarStyleId, ParsedAvatarData } from "@/types/avatar";

/**
 * メールアドレスから一貫したシード値を生成
 * 同じメールアドレスからは常に同じシードが生成される
 */
export function generateSeedFromEmail(email: string): string {
  try {
    // base64エンコードして記号を除去
    const encoded = btoa(email.toLowerCase().trim());
    return encoded.replace(/[^a-zA-Z0-9]/g, "").substring(0, 20);
  } catch {
    // btoa失敗時のフォールバック（非ASCII文字等）
    return email.replace(/[^a-zA-Z0-9]/g, "").substring(0, 20);
  }
}

/**
 * DiceBearアバターのURLを生成
 * @param style - アバタースタイル
 * @param seed - シード値（ユーザーごとに一貫した値を使用）
 * @returns DiceBear API のURL
 */
export function generateDicebearUrl(style: AvatarStyleId, seed: string): string {
  // URLパラメータをエンコード
  const encodedSeed = encodeURIComponent(seed);
  return `${DICEBEAR_API_BASE}/${style}/svg?seed=${encodedSeed}`;
}

/**
 * users.imageカラムの値をパース
 *
 * 対応形式:
 * - "dicebear:bottts-neutral:abc123" -> DiceBear形式
 * - "https://..." -> 外部URL（後方互換性）
 * - null/undefined -> デフォルト
 *
 * @param imageValue - DBから取得した画像値
 * @returns パース結果
 */
export function parseAvatarData(imageValue: string | null | undefined): ParsedAvatarData {
  // null/undefined -> デフォルト
  if (!imageValue) {
    return { type: "default" };
  }

  // DiceBear形式: "dicebear:style:seed"
  if (imageValue.startsWith("dicebear:")) {
    const parts = imageValue.split(":");
    if (parts.length === 3) {
      const [, style, seed] = parts;

      // スタイルの検証
      const isValidStyle = AVATAR_STYLES.some((s) => s.id === style);
      if (isValidStyle && seed) {
        return {
          type: "dicebear",
          style: style as AvatarStyleId,
          seed,
        };
      }
    }
    // 不正な形式の場合はデフォルト
    return { type: "default" };
  }

  // 外部URL形式（後方互換性）
  if (imageValue.startsWith("http://") || imageValue.startsWith("https://")) {
    return {
      type: "url",
      url: imageValue,
    };
  }

  // 不明な形式はデフォルト
  return { type: "default" };
}

/**
 * ユーザーのアバターURLを取得
 *
 * @param email - ユーザーのメールアドレス
 * @param imageValue - DBのusers.imageカラムの値
 * @returns 表示用のアバターURL
 */
export function getUserAvatarUrl(email: string, imageValue: string | null | undefined): string {
  const parsed = parseAvatarData(imageValue);

  switch (parsed.type) {
    case "dicebear":
      if (parsed.style && parsed.seed) {
        return generateDicebearUrl(parsed.style, parsed.seed);
      }
      return generateDicebearUrl(DEFAULT_AVATAR_STYLE, generateSeedFromEmail(email));

    case "url":
      if (parsed.url) {
        return parsed.url;
      }
      return generateDicebearUrl(DEFAULT_AVATAR_STYLE, generateSeedFromEmail(email));

    case "default": {
      // デフォルト: メールからシードを生成してbottts-neutralを使用
      const seed = generateSeedFromEmail(email);
      return generateDicebearUrl(DEFAULT_AVATAR_STYLE, seed);
    }

    default: {
      // 型安全性のためのフォールバック
      const defaultSeed = generateSeedFromEmail(email);
      return generateDicebearUrl(DEFAULT_AVATAR_STYLE, defaultSeed);
    }
  }
}

/**
 * アバター設定をDB保存用の文字列に変換
 *
 * @param style - 選択されたスタイル
 * @param seed - シード値
 * @returns DB保存用の文字列 (例: "dicebear:bottts-neutral:abc123")
 */
export function serializeAvatarData(style: AvatarStyleId, seed: string): string {
  return `dicebear:${style}:${seed}`;
}

/**
 * アバター画像のフォールバック用SVG Data URLを生成
 * 画像読み込み失敗時に表示する
 */
export function getAvatarFallbackUrl(): string {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
      <rect width="200" height="200" fill="#e5e7eb"/>
      <text
        x="50%"
        y="50%"
        dominant-baseline="middle"
        text-anchor="middle"
        fill="#9ca3af"
        font-size="80"
        font-family="system-ui, -apple-system, sans-serif"
      >?</text>
    </svg>
  `;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}
